import { useCallback, useState } from "react"

import styles from "../ConfirmDialog.module.scss"

import { graphql } from "@/company_admin/gql/__generated__"
import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import { useCompanyAdminMutation } from "@/provider/apollo/Apollo"

// --- GraphQL ---

const RecreateSettlement = graphql(/* GraphQL */ `
	mutation RecreateSettlement($input: RecreateSettlementInput!) {
		recreateSettlement(input: $input) {
			settlement {
				id
			}
		}
	}
`)

type Props = {
	onRecreateComplete: () => void
}

export const useArchiveAndRecreateDialog = ({ onRecreateComplete }: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const [targetSettlement, setTargetSettlement] = useState<{
		id: string
		yearMonth: { year: number; month: number }
	} | null>(null)
	const [error, setError] = useState<string | null>(null)

	const [recreateSettlement, { loading }] = useCompanyAdminMutation(
		RecreateSettlement,
		{
			context: { disableErrorDialog: true },
		}
	)

	const openDialog = useCallback(
		(settlementId: string, yearMonth: { year: number; month: number }) => {
			setTargetSettlement({ id: settlementId, yearMonth })
			setError(null)
			setIsOpen(true)
		},
		[]
	)

	const handleRecreate = useCallback(async () => {
		if (!targetSettlement) return
		setError(null)
		try {
			await recreateSettlement({
				variables: {
					input: { settlementId: targetSettlement.id },
				},
			})
			setIsOpen(false)
			onRecreateComplete()
		} catch (e) {
			const msg = e instanceof Error ? e.message : "再作成に失敗しました。"
			setError(msg)
			console.error("recreateSettlement error:", e)
		}
	}, [targetSettlement, recreateSettlement, onRecreateComplete])

	const render = useCallback(() => {
		return (
			<ModalDialog isOpen={isOpen} onOpenChange={setIsOpen}>
				<div className={styles.dialog_content}>
					<h2 className={styles.title}>相殺明細のアーカイブ&再作成</h2>
					{error && <p className={styles.error}>{error}</p>}
					<p className={styles.message}>
						{targetSettlement
							? `${targetSettlement.yearMonth.year}年${targetSettlement.yearMonth.month}月の相殺明細をアーカイブし、最新データで再作成します。この操作は取り消せません。`
							: ""}
					</p>
					<div className={styles.button_container}>
						<button
							type="button"
							className={styles.cancel_button}
							onClick={() => setIsOpen(false)}
							disabled={loading}
						>
							キャンセル
						</button>
						<button
							type="button"
							className={styles.confirm_button}
							onClick={handleRecreate}
							disabled={loading}
						>
							{loading ? "処理中..." : "再作成する"}
						</button>
					</div>
				</div>
			</ModalDialog>
		)
	}, [isOpen, targetSettlement, error, loading, handleRecreate])

	return {
		render,
		openDialog,
	}
}
